import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Calculator, Loader2, AlertCircle, BookOpen, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import ProGate from "@/components/ProGate";
import CalcDisclaimer from "@/components/CalcDisclaimer";
import ElbowDiagram from "@/components/ElbowDiagram";
import {
  elbows45_imperial as oe45imp,
  elbows45_metric   as oe45met,
  elbows90_imperial as oe90imp,
  elbows90_metric   as oe90met,
} from "@/data/orbitalElbows";

/* ─────────────────────────────────────────────────────────────
   i18n — Pipefitter strings for sk / en / cs / de / pl
   ───────────────────────────────────────────────────────────── */
const L = {
  sk: {
    tag: "POTRUBÁRI",
    title: "Tabuľky pre potrubárov",
    sub: "Segmenty · Odskoky · Oblúkové dĺžky · Kolená",
    loadingTables: "Načítavam tabuľky…",
    disclaimer: "Potrubárske tabuľky: nezaručujeme bezchybnosť pri každom rozmere ani prípadné odchýlky od pôvodných výkresov. Pred rezaním materiálu si vždy hodnoty premerajte na vzorovom kuse.",
    tabs: {
      segment: "Segment / DN × uhol",
      offset:  "Odskok (Offset)",
      arc:     "Oblúk (K, F)",
      elbow:   "Kolená DIN / ISO",
      orbital: "Orbitálne kolená",
      iso:     "Referenčné tabuľky",
    },
    segmentHelp: "Hodnoty v mm — segmentová dĺžka pre rúru daného DN a uhla odskoku.",
    angleByDn: "Uhol \\ DN",
    formula: "Vzorec",
    offsetH: "Odskok h (mm)",
    offsetR: "Polomer ohybu r (mm)",
    angleDeg: (a, b) => `Uhol (°) — ${a}–${b}`,
    computeZ: "Vypočítať Z",
    computeHL: "Vypočítať H a L",
    radius: "Polomer R (mm)",
    zLen: "Z (segmentová dĺžka)",
    height: "H (výška)",
    arcLen: "L (dĺžka oblúka)",
    fCoeffTitle: "Koeficienty f1, f2",
    kfCoeffTitle: "Koeficienty K, F",
    legendTitle: "Čo je čo? — vysvetlivky pre laika",
    legendShow: "Zobraziť vysvetlivky",
    legendHide: "Skryť vysvetlivky",
    legend: {
      R: "Polomer ohybu rúry (mm) — od osi rúry. Čím väčší, tým „mäkší\" oblúk.",
      H: "Výška skoku (offset) — kolmá vzdialenosť medzi pôvodnou a novou osou rúry (mm).",
      L: "Dĺžka oblúka — koľko milimetrov rúry spotrebuješ na ohyb.",
      Z: "Segmentová dĺžka — celková dĺžka šikmého kusu medzi dvoma kolenami.",
      K: "Koeficient K = H/R. Bezrozmerné číslo, hovorí ako veľký je oblúk vzhľadom k polomeru.",
      F: "Uhol v radiánoch (F = stupne × π / 180). Používa sa pri výpočte L = R·F.",
      angle: "Uhol ohybu v stupňoch (najčastejšie 45° alebo 90°).",
      DN: "Nominálny priemer rúry (DN50 = približne 50 mm, DN100 = 100 mm).",
    },
    /* ISO ref tabuľky pokračujú nižšie */
    elbowCardTitle: (title) => `${title} — kolená 90° / 45° (mm)`,
    orbital: {
      title: "Orbitálne kolená · predĺžený tangens",
      desc: "Predĺžené kolená pre orbitálne zváranie hygienických nerezových rúr. Dlhší tangens (l1, l2) umožňuje uchytenie orbitálnej zváracej hlavice tesne pri hrane kolena. Norma DIN 11865 (imperial / OD) a DIN 11866 (metric / DN).",
      footnotes: "d1 — vonkajší priemer rúry (mm) · s1 — hrúbka steny (mm) · l1, l2 — dĺžky tangenciálnych rovných úsekov (mm) · r1 — polomer ohybu (mm) · * predĺžená verzia · ** hrúbka steny nezodpovedá DIN 11865/11866 (na požiadanie).",
      diagram: {
        title: "Rozmery kolena — vysvetlivky",
        d1: "d1 — vonkajší priemer rúry (mm)",
        s1: "s1 — hrúbka steny (mm)",
        l1: "l1 — dĺžka tangenciálneho úseku 1 (mm)",
        l2: "l2 — dĺžka tangenciálneho úseku 2 (mm)",
        r1: "r1 — polomer ohybu (stred, mm)",
        alpha: "α — uhol ohybu (napr. 45°, 90°)",
        caption: "Označenia zodpovedajú stĺpcom v tabuľkách nižšie. Dlhšie tangenciálne úseky (l1, l2) umožňujú uchytenie orbitálnej zváracej hlavice priamo pri hrane kolena.",
      },
    },
    computeFailed: "Výpočet zlyhal",
    /* ISO referenčné tabuľky */
    iso: {
      loading: "Načítavam referenčné tabuľky…",
      loadFailed: "Načítanie zlyhalo",
      bannerTag: "Referenčné inžinierske dáta · vlastná zostavená zbierka",
      bannerP1Part1: "Hodnoty zostavené z verejne dostupných",
      bannerP1Highlight: "katalógov výrobcov rúrok",
      bannerP1Part2: "(Sandvik, Outokumpu, Mannesmann, Marcegaglia), inžinierskych príručiek a tradičných remeselných tabuliek. Označenia ako „ISO 1127\" sa používajú ako bežné priemyselné menovky — táto aplikácia",
      bannerP1Reprod: "nereprodukuje",
      bannerP1Part3: "žiadne autorsky chránené ISO dokumenty.",
      bannerP2Part1: "Pre certifikačné účely, dimenzačné inšpekcie alebo zmluvné záväzky vždy použite originálne normy z",
      bannerP2Part2: "alebo národného úradu (ÚNMS SR pre Slovensko).",
      filterPlaceholder: "Filtrovať podľa OD / DN / NPS…",
      noResults: (q) => `Žiadne výsledky pre „${q}".`,
      footerDisclaimer: "⚠ Tigik za prípadné odchýlky od oficiálnych noriem nezodpovedá. Hodnoty sú orientačné a slúžia ako pomôcka v dielni — nie ako certifikačný dokument.",
      subTabs: {
        iso1127:  { label: "Nerezové rúrky",       desc: "Rozmery & hrúbky stien" },
        iso4200:  { label: "Hmotnosti rúrok",      desc: "kg/m podľa rozmeru" },
        iso2768:  { label: "Voľné tolerancie",     desc: "Lineárne + uhlové" },
        iso6708:  { label: "DN ↔ NPS prevodník",   desc: "Európa ↔ USA" },
        iso15649: { label: "PN tlakové triedy",    desc: "Petrochémia" },
        iso13480: { label: "Pharma & Food finish", desc: "Ra μm, zvarové triedy" },
      },
      title1127:    "Štandardné rozmery nerezových rúrok (referenčné, ref. ISO 1127)",
      title4200:    "Hmotnosti uhlíkových rúrok (kg/m, vypočítané z fyziky · ref. ISO 4200)",
      title2768lin: "Voľné lineárne tolerancie (mm · ref. ISO 2768-1)",
      title2768ang: "Voľné uhlové tolerancie (ref. ISO 2768-2)",
      title6708:    "Prevodník DN ↔ NPS (Európa ↔ USA · ref. ISO 6708)",
      title15649:   "PN tlakové triedy pre petrochémiu (ref. ISO 15649)",
      title13480:   "Povrchové úpravy pre Food & Pharma (Ra μm · ref. ISO 13480)",
      title13458:   "Triedy zvarov pre potravinárstvo / pharma (ref. ISO 13458)",
      colOdMm: "OD (mm)", colWallMm: "Stena (mm)", colKgM: "kg / m",
      colRange: "Rozsah", colFineLin: "f (jemné)", colMedLin: "m (stred.)", colCoarseLin: "c (hrubé)", colVeryCoarseLin: "v (veľmi hrubé)",
      colShortSide: "Krátka strana", colHFine: "H (jemné)", colKMed: "K (stred.)", colLCoarse: "L (hrubé)",
      colDn: "DN", colNps: "NPS (palce)",
      colPn: "PN (bar)", colAnsiClass: "ANSI Class", colUseCase: "Typické použitie",
      colCode: "Kód", colRaMax: "Ra max (μm)", colMethod: "Metóda", colUse: "Použitie",
      colClass: "Trieda", colDesc: "Popis", colDefects: "Povolené defekty",
    },
    /* Lookup texts for backend-returned codes (PN, finish-code, weld-class) */
    pnUse: {
      6:   "Nízkotlakové rozvody, voda, vzduch",
      10:  "Štandardné priemyselné rozvody, voda, plyn LP",
      16:  "Štandard pre paru NT, chemicky neagresívne média",
      25:  "Procesné potrubia, para ST, ropa",
      40:  "Petrochémia ST, oleje, agresívne kvapaliny",
      63:  "Procesné potrubia VT, parné rozvody",
      100: "Vysokotlakové procesy, hydraulika, para VT",
      160: "Petrochémia, rafinérie, superkritická para",
      250: "Ultra-VT — chemická syntéza, ropné polia",
      320: "Špeciálne aplikácie, jadrový priemysel",
      400: "Ultra-špeciálne, výskum, vesmírny priemysel",
    },
    finishMethod: {
      "1D":   "Mechanické (kefovanie)",
      "2B":   "Valcované za studena",
      "2BA":  "Žiarený a leštený",
      "180G": "Brúsenie zrnom 180",
      "240G": "Brúsenie zrnom 240",
      "320G": "Brúsenie zrnom 320",
      "400G": "Brúsenie zrnom 400",
      "EP":   "Elektrolytické leštenie",
      "EP+":  "EP + pasivácia",
    },
    finishUse: {
      "1D":   "Vonkajšie povrchy, neagresívne médium",
      "2B":   "Štandardná pharma, voda WFI",
      "2BA":  "Pharma, kozmetika, čisté médium",
      "180G": "Potravinárstvo (mlieko, pivo)",
      "240G": "Vyššia trieda potravinárstva",
      "320G": "Pharma štandard",
      "400G": "Vysoká pharma, biotech",
      "EP":   "Ultra-pharma, semiconductor, vakuum",
      "EP+":  "Top-tier — bioreaktory, sterilné injekcie",
    },
    weldDesc: {
      "1": "Plnoprovaru s odzhora hladkým povrchom",
      "2": "Plnoprovaru s miernymi nedokonalosťami",
      "3": "Štandardný zvar s viditeľnou koreňovou stranou",
      "4": "Funkčný zvar bez kozmetických nárokov",
    },
    weldDefects: {
      "1": "Žiadne (≤ 0,1 mm)",
      "2": "≤ 0,3 mm priemer pórov",
      "3": "≤ 0,5 mm",
      "4": "≤ 1,0 mm",
    },
    weldUse: {
      "1": "Pharma WFI, biotech, sterilné média",
      "2": "Potravinárstvo, mliekarne",
      "3": "Bežné procesné potrubia",
      "4": "Pomocné rozvody, drenáž",
    },
  },

  en: {
    tag: "PIPEFITTERS",
    title: "Pipefitter tables",
    sub: "Segments · Offsets · Arc lengths · Elbows",
    loadingTables: "Loading tables…",
    disclaimer: "Pipefitter tables: we don't guarantee accuracy for every dimension or possible deviations from original drawings. Always verify on a sample piece before cutting material.",
    tabs: {
      segment: "Segment / DN × angle",
      offset:  "Offset",
      arc:     "Arc (K, F)",
      elbow:   "Elbows DIN / ISO",
      orbital: "Orbital elbows",
      iso:     "Reference tables",
    },
    segmentHelp: "Values in mm — segment length for a pipe of given DN and offset angle.",
    angleByDn: "Angle \\ DN",
    formula: "Formula",
    offsetH: "Offset h (mm)",
    offsetR: "Bend radius r (mm)",
    angleDeg: (a, b) => `Angle (°) — ${a}–${b}`,
    computeZ: "Calculate Z",
    computeHL: "Calculate H and L",
    radius: "Radius R (mm)",
    zLen: "Z (segment length)",
    height: "H (height)",
    arcLen: "L (arc length)",
    fCoeffTitle: "Coefficients f1, f2",
    kfCoeffTitle: "Coefficients K, F",
    legendTitle: "What's what? — explanations for beginners",
    legendShow: "Show legend",
    legendHide: "Hide legend",
    legend: {
      R: "Pipe bend radius (mm) — measured from the pipe axis. Larger = softer arc.",
      H: "Offset height — perpendicular distance between original and new pipe axis (mm).",
      L: "Arc length — millimetres of pipe consumed by the bend.",
      Z: "Segment length — total length of the inclined piece between two elbows.",
      K: "Coefficient K = H/R. Dimensionless number — relates arc size to radius.",
      F: "Angle in radians (F = degrees × π / 180). Used in L = R·F.",
      angle: "Bend angle in degrees (most common 45° or 90°).",
      DN: "Nominal pipe diameter (DN50 ≈ 50 mm, DN100 ≈ 100 mm).",
    },
    elbowCardTitle: (title) => `${title} — elbows 90° / 45° (mm)`,
    orbital: {
      title: "Orbital elbows · extended tangent",
      desc: "Extended-tangent hygienic elbows for orbital welding of stainless tubes. Longer tangent lengths (l1, l2) allow the orbital head to clamp directly at the elbow edge. Standard: DIN 11865 (imperial / OD) and DIN 11866 (metric / DN).",
      footnotes: "d1 — outer tube diameter (mm) · s1 — wall thickness (mm) · l1, l2 — tangential straight section lengths (mm) · r1 — bend radius (mm) · * extended version · ** wall thickness does not comply with DIN 11865/11866 (on request).",
      diagram: {
        title: "Elbow dimensions — legend",
        d1: "d1 — outer tube diameter (mm)",
        s1: "s1 — wall thickness (mm)",
        l1: "l1 — tangent length 1 (mm)",
        l2: "l2 — tangent length 2 (mm)",
        r1: "r1 — bend radius (centre, mm)",
        alpha: "α — bend angle (e.g. 45°, 90°)",
        caption: "Labels correspond to columns in the tables below. Extended tangents (l1, l2) allow orbital welding heads to clamp directly at the elbow edge.",
      },
    },
    computeFailed: "Compute failed",
    iso: {
      loading: "Loading reference tables…",
      loadFailed: "Failed to load",
      bannerTag: "Engineering reference data · curated collection",
      bannerP1Part1: "Values compiled from publicly available",
      bannerP1Highlight: "pipe manufacturer catalogues",
      bannerP1Part2: "(Sandvik, Outokumpu, Mannesmann, Marcegaglia), engineering handbooks and traditional trade tables. Designations like \"ISO 1127\" are used as common industry shorthand — this application",
      bannerP1Reprod: "does not reproduce",
      bannerP1Part3: "any copyrighted ISO documents.",
      bannerP2Part1: "For certification, dimensional inspection or contractual obligations always use the original standards from",
      bannerP2Part2: "or your national standards body (e.g. BSI for UK, ANSI for USA).",
      filterPlaceholder: "Filter by OD / DN / NPS…",
      noResults: (q) => `No results for \"${q}\".`,
      footerDisclaimer: "⚠ Tigik is not liable for any deviation from official standards. Values are indicative — workshop reference only, not a certification document.",
      subTabs: {
        iso1127:  { label: "Stainless tubes",       desc: "Sizes & wall thickness" },
        iso4200:  { label: "Pipe masses",            desc: "kg/m by dimension" },
        iso2768:  { label: "General tolerances",     desc: "Linear + angular" },
        iso6708:  { label: "DN ↔ NPS converter",     desc: "Europe ↔ USA" },
        iso15649: { label: "PN pressure classes",    desc: "Petrochemicals" },
        iso13480: { label: "Pharma & Food finish",   desc: "Ra μm, weld classes" },
      },
      title1127:    "Standard stainless tube sizes (reference, ref. ISO 1127)",
      title4200:    "Carbon-steel tube masses (kg/m, calculated from physics · ref. ISO 4200)",
      title2768lin: "General linear tolerances (mm · ref. ISO 2768-1)",
      title2768ang: "General angular tolerances (ref. ISO 2768-2)",
      title6708:    "DN ↔ NPS converter (Europe ↔ USA · ref. ISO 6708)",
      title15649:   "PN pressure classes for petrochemicals (ref. ISO 15649)",
      title13480:   "Surface finishes for Food & Pharma (Ra μm · ref. ISO 13480)",
      title13458:   "Weld classes for food / pharma (ref. ISO 13458)",
      colOdMm: "OD (mm)", colWallMm: "Wall (mm)", colKgM: "kg / m",
      colRange: "Range", colFineLin: "f (fine)", colMedLin: "m (medium)", colCoarseLin: "c (coarse)", colVeryCoarseLin: "v (very coarse)",
      colShortSide: "Short side", colHFine: "H (fine)", colKMed: "K (medium)", colLCoarse: "L (coarse)",
      colDn: "DN", colNps: "NPS (inches)",
      colPn: "PN (bar)", colAnsiClass: "ANSI Class", colUseCase: "Typical use",
      colCode: "Code", colRaMax: "Ra max (μm)", colMethod: "Method", colUse: "Use",
      colClass: "Class", colDesc: "Description", colDefects: "Allowed defects",
    },
    pnUse: {
      6:   "Low-pressure piping, water, air",
      10:  "Standard industrial piping, water, LP gas",
      16:  "Standard for low-pressure steam, neutral chemicals",
      25:  "Process piping, MP steam, oil",
      40:  "Petrochemicals MP, oils, aggressive liquids",
      63:  "HP process piping, steam distribution",
      100: "High-pressure processes, hydraulics, HP steam",
      160: "Petrochemicals, refineries, supercritical steam",
      250: "Ultra-HP — chemical synthesis, oil fields",
      320: "Special applications, nuclear industry",
      400: "Ultra-special, R&D, aerospace",
    },
    finishMethod: {
      "1D":   "Mechanical (brushed)",
      "2B":   "Cold-rolled",
      "2BA":  "Bright annealed & polished",
      "180G": "Grain-180 grinding",
      "240G": "Grain-240 grinding",
      "320G": "Grain-320 grinding",
      "400G": "Grain-400 grinding",
      "EP":   "Electropolished",
      "EP+":  "EP + passivation",
    },
    finishUse: {
      "1D":   "Outer surfaces, non-aggressive media",
      "2B":   "Standard pharma, WFI water",
      "2BA":  "Pharma, cosmetics, clean media",
      "180G": "Food (dairy, brewery)",
      "240G": "Higher-grade food",
      "320G": "Pharma standard",
      "400G": "High pharma, biotech",
      "EP":   "Ultra-pharma, semiconductor, vacuum",
      "EP+":  "Top-tier — bioreactors, sterile injection",
    },
    weldDesc: {
      "1": "Full penetration with smooth crown",
      "2": "Full penetration with minor imperfections",
      "3": "Standard weld with visible root side",
      "4": "Functional weld, no cosmetic requirements",
    },
    weldDefects: {
      "1": "None (≤ 0.1 mm)",
      "2": "≤ 0.3 mm pore diameter",
      "3": "≤ 0.5 mm",
      "4": "≤ 1.0 mm",
    },
    weldUse: {
      "1": "Pharma WFI, biotech, sterile media",
      "2": "Food, dairies",
      "3": "Common process piping",
      "4": "Auxiliary lines, drainage",
    },
  },

  cs: {
    tag: "POTRUBÁŘI",
    title: "Tabulky pro potrubáře",
    sub: "Segmenty · Odskoky · Obloukové délky · Kolena",
    loadingTables: "Načítám tabulky…",
    disclaimer: "Potrubářské tabulky: nezaručujeme bezchybnost u každého rozměru ani případné odchylky od původních výkresů. Před řezáním materiálu si vždy hodnoty přeměřte na vzorovém kuse.",
    tabs: {
      segment: "Segment / DN × úhel",
      offset:  "Odskok (Offset)",
      arc:     "Oblouk (K, F)",
      elbow:   "Kolena DIN / ISO",
      orbital: "Orbitální kolena",
      iso:     "Referenční tabulky",
    },
    segmentHelp: "Hodnoty v mm — segmentová délka pro trubku daného DN a úhlu odskoku.",
    angleByDn: "Úhel \\ DN",
    formula: "Vzorec",
    offsetH: "Odskok h (mm)",
    offsetR: "Poloměr ohybu r (mm)",
    angleDeg: (a, b) => `Úhel (°) — ${a}–${b}`,
    computeZ: "Vypočítat Z",
    computeHL: "Vypočítat H a L",
    radius: "Poloměr R (mm)",
    zLen: "Z (segmentová délka)",
    height: "H (výška)",
    arcLen: "L (délka oblouku)",
    fCoeffTitle: "Koeficienty f1, f2",
    kfCoeffTitle: "Koeficienty K, F",
    legendTitle: "Co je co? — vysvětlivky pro laika",
    legendShow: "Zobrazit vysvětlivky",
    legendHide: "Skrýt vysvětlivky",
    legend: {
      R: "Poloměr ohybu trubky (mm) — od osy trubky. Větší = měkčí oblouk.",
      H: "Výška skoku (offset) — kolmá vzdálenost mezi původní a novou osou trubky (mm).",
      L: "Délka oblouku — kolik milimetrů trubky spotřebuješ na ohyb.",
      Z: "Segmentová délka — celková délka šikmého kusu mezi dvěma kolenami.",
      K: "Koeficient K = H/R. Bezrozměrné číslo, vztah oblouku k poloměru.",
      F: "Úhel v radiánech (F = stupně × π / 180). Používá se v L = R·F.",
      angle: "Úhel ohybu ve stupních (nejčastěji 45° nebo 90°).",
      DN: "Nominální průměr trubky (DN50 ≈ 50 mm, DN100 ≈ 100 mm).",
    },
    elbowCardTitle: (title) => `${title} — kolena 90° / 45° (mm)`,
    orbital: {
      title: "Orbitální kolena · prodloužený tangens",
      desc: "Prodloužená kolena pro orbitální svařování hygienických nerezových trubek. Delší tangens (l1, l2) umožňuje uchycení orbitální hlavy těsně u hrany kolena. Norma DIN 11865 (imperial / OD) a DIN 11866 (metric / DN).",
      footnotes: "d1 — vnější průměr trubky (mm) · s1 — tloušťka stěny (mm) · l1, l2 — délky tangenciálních rovných úseků (mm) · r1 — poloměr ohybu (mm) · * prodloužená verze · ** tloušťka stěny neodpovídá DIN 11865/11866 (na vyžádání).",
      diagram: {
        title: "Rozměry kolena — legenda",
        d1: "d1 — vnější průměr trubky (mm)",
        s1: "s1 — tloušťka stěny (mm)",
        l1: "l1 — délka tangenciálního úseku 1 (mm)",
        l2: "l2 — délka tangenciálního úseku 2 (mm)",
        r1: "r1 — poloměr ohybu (střed, mm)",
        alpha: "α — úhel ohybu (např. 45°, 90°)",
        caption: "Označení odpovídají sloupcům v tabulkách níže. Delší tangenciální úseky umožňují uchycení orbitální svařovací hlavy přímo u hrany kolena.",
      },
    },
    computeFailed: "Výpočet selhal",
    iso: {
      loading: "Načítám referenční tabulky…",
      loadFailed: "Načtení selhalo",
      bannerTag: "Referenční inženýrská data · vlastní sestavená sbírka",
      bannerP1Part1: "Hodnoty sestavené z veřejně dostupných",
      bannerP1Highlight: "katalogů výrobců trubek",
      bannerP1Part2: "(Sandvik, Outokumpu, Mannesmann, Marcegaglia), inženýrských příruček a tradičních řemeslnických tabulek. Označení jako „ISO 1127\" se používají jako běžné průmyslové názvy — tato aplikace",
      bannerP1Reprod: "nereprodukuje",
      bannerP1Part3: "žádné autorsky chráněné ISO dokumenty.",
      bannerP2Part1: "Pro certifikační účely, dimenzační inspekce nebo smluvní závazky vždy použijte originální normy z",
      bannerP2Part2: "nebo národního úřadu (ÚNMZ pro ČR).",
      filterPlaceholder: "Filtrovat podle OD / DN / NPS…",
      noResults: (q) => `Žádné výsledky pro „${q}\".`,
      footerDisclaimer: "⚠ Tigik za případné odchylky od oficiálních norem neodpovídá. Hodnoty jsou orientační a slouží jako pomůcka v dílně — ne jako certifikační dokument.",
      subTabs: {
        iso1127:  { label: "Nerezové trubky",      desc: "Rozměry & tloušťky stěn" },
        iso4200:  { label: "Hmotnosti trubek",      desc: "kg/m podle rozměru" },
        iso2768:  { label: "Volné tolerance",       desc: "Lineární + úhlové" },
        iso6708:  { label: "DN ↔ NPS převodník",    desc: "Evropa ↔ USA" },
        iso15649: { label: "PN tlakové třídy",      desc: "Petrochemie" },
        iso13480: { label: "Pharma & Food finish",  desc: "Ra μm, svarové třídy" },
      },
      title1127:    "Standardní rozměry nerezových trubek (referenční, ref. ISO 1127)",
      title4200:    "Hmotnosti uhlíkových trubek (kg/m, vypočtené z fyziky · ref. ISO 4200)",
      title2768lin: "Volné lineární tolerance (mm · ref. ISO 2768-1)",
      title2768ang: "Volné úhlové tolerance (ref. ISO 2768-2)",
      title6708:    "Převodník DN ↔ NPS (Evropa ↔ USA · ref. ISO 6708)",
      title15649:   "PN tlakové třídy pro petrochemii (ref. ISO 15649)",
      title13480:   "Povrchové úpravy pro Food & Pharma (Ra μm · ref. ISO 13480)",
      title13458:   "Třídy svarů pro potravinářství / pharma (ref. ISO 13458)",
      colOdMm: "OD (mm)", colWallMm: "Stěna (mm)", colKgM: "kg / m",
      colRange: "Rozsah", colFineLin: "f (jemné)", colMedLin: "m (střední)", colCoarseLin: "c (hrubé)", colVeryCoarseLin: "v (velmi hrubé)",
      colShortSide: "Krátká strana", colHFine: "H (jemné)", colKMed: "K (střední)", colLCoarse: "L (hrubé)",
      colDn: "DN", colNps: "NPS (palce)",
      colPn: "PN (bar)", colAnsiClass: "ANSI Class", colUseCase: "Typické použití",
      colCode: "Kód", colRaMax: "Ra max (μm)", colMethod: "Metoda", colUse: "Použití",
      colClass: "Třída", colDesc: "Popis", colDefects: "Povolené defekty",
    },
    pnUse: {
      6:   "Nízkotlaké rozvody, voda, vzduch",
      10:  "Standardní průmyslové rozvody, voda, plyn LP",
      16:  "Standard pro páru NT, chemicky neagresivní média",
      25:  "Procesní potrubí, pára ST, ropa",
      40:  "Petrochemie ST, oleje, agresivní kapaliny",
      63:  "Procesní potrubí VT, parní rozvody",
      100: "Vysokotlaké procesy, hydraulika, pára VT",
      160: "Petrochemie, rafinérie, superkritická pára",
      250: "Ultra-VT — chemická syntéza, ropná pole",
      320: "Speciální aplikace, jaderný průmysl",
      400: "Ultra-speciální, výzkum, kosmonautika",
    },
    finishMethod: {
      "1D":   "Mechanické (kartáčování)",
      "2B":   "Válcované za studena",
      "2BA":  "Žíhané a leštěné",
      "180G": "Broušení zrnem 180",
      "240G": "Broušení zrnem 240",
      "320G": "Broušení zrnem 320",
      "400G": "Broušení zrnem 400",
      "EP":   "Elektrolytické leštění",
      "EP+":  "EP + pasivace",
    },
    finishUse: {
      "1D":   "Vnější povrchy, neagresivní médium",
      "2B":   "Standardní pharma, voda WFI",
      "2BA":  "Pharma, kosmetika, čisté médium",
      "180G": "Potravinářství (mléko, pivo)",
      "240G": "Vyšší třída potravinářství",
      "320G": "Pharma standard",
      "400G": "Vysoká pharma, biotech",
      "EP":   "Ultra-pharma, semiconductor, vakuum",
      "EP+":  "Top-tier — bioreaktory, sterilní injekce",
    },
    weldDesc: {
      "1": "Plnoprovaru s nahoře hladkým povrchem",
      "2": "Plnoprovaru s mírnými nedokonalostmi",
      "3": "Standardní svar s viditelnou kořenovou stranou",
      "4": "Funkční svar bez kosmetických nároků",
    },
    weldDefects: {
      "1": "Žádné (≤ 0,1 mm)",
      "2": "≤ 0,3 mm průměr pórů",
      "3": "≤ 0,5 mm",
      "4": "≤ 1,0 mm",
    },
    weldUse: {
      "1": "Pharma WFI, biotech, sterilní média",
      "2": "Potravinářství, mlékárny",
      "3": "Běžná procesní potrubí",
      "4": "Pomocné rozvody, drenáž",
    },
  },

  de: {
    tag: "ROHRLEGER",
    title: "Tabellen für Rohrleger",
    sub: "Segmente · Versätze · Bogenlängen · Bögen",
    loadingTables: "Lade Tabellen…",
    disclaimer: "Rohrleger-Tabellen: Wir garantieren keine Fehlerfreiheit jeder Abmessung oder mögliche Abweichungen von Originalzeichnungen. Werte vor dem Schneiden immer am Musterstück prüfen.",
    tabs: {
      segment: "Segment / DN × Winkel",
      offset:  "Versatz (Offset)",
      arc:     "Bogen (K, F)",
      elbow:   "Bögen DIN / ISO",
      orbital: "Orbital-Bögen",
      iso:     "Referenz-Tabellen",
    },
    segmentHelp: "Werte in mm — Segmentlänge für ein Rohr gegebener DN und Versatzwinkel.",
    angleByDn: "Winkel \\ DN",
    formula: "Formel",
    offsetH: "Versatz h (mm)",
    offsetR: "Biegeradius r (mm)",
    angleDeg: (a, b) => `Winkel (°) — ${a}–${b}`,
    computeZ: "Z berechnen",
    computeHL: "H und L berechnen",
    radius: "Radius R (mm)",
    zLen: "Z (Segmentlänge)",
    height: "H (Höhe)",
    arcLen: "L (Bogenlänge)",
    fCoeffTitle: "Koeffizienten f1, f2",
    kfCoeffTitle: "Koeffizienten K, F",
    legendTitle: "Was ist was? — Erklärungen für Anfänger",
    legendShow: "Legende anzeigen",
    legendHide: "Legende ausblenden",
    legend: {
      R: "Rohrbiegeradius (mm) — gemessen von der Rohrachse. Größer = weicherer Bogen.",
      H: "Versatzhöhe — senkrechter Abstand zwischen ursprünglicher und neuer Rohrachse (mm).",
      L: "Bogenlänge — Millimeter Rohr, die für den Bogen benötigt werden.",
      Z: "Segmentlänge — gesamte Länge des schrägen Stücks zwischen zwei Bögen.",
      K: "Koeffizient K = H/R. Dimensionslose Zahl — Verhältnis Bogen zu Radius.",
      F: "Winkel im Bogenmaß (F = Grad × π / 180). Verwendet in L = R·F.",
      angle: "Biegewinkel in Grad (häufig 45° oder 90°).",
      DN: "Nenndurchmesser des Rohrs (DN50 ≈ 50 mm, DN100 ≈ 100 mm).",
    },
    elbowCardTitle: (title) => `${title} — Bögen 90° / 45° (mm)`,
    orbital: {
      title: "Orbital-Bögen · verlängerter Tangens",
      desc: "Verlängerte Bögen für das Orbitalschweißen hygienischer Edelstahlrohre. Der längere Tangens (l1, l2) ermöglicht das Aufsetzen des Orbitalkopfes direkt an der Bogenkante. Norm: DIN 11865 (Imperial / OD) und DIN 11866 (Metric / DN).",
      footnotes: "d1 — Rohraußendurchmesser (mm) · s1 — Wandstärke (mm) · l1, l2 — Längen der tangentialen Geradenabschnitte (mm) · r1 — Biegeradius (mm) · * verlängerte Version · ** Wandstärke entspricht nicht DIN 11865/11866 (auf Anfrage).",
      diagram: {
        title: "Bogenmaße — Legende",
        d1: "d1 — Rohraußendurchmesser (mm)",
        s1: "s1 — Wandstärke (mm)",
        l1: "l1 — Länge des Tangens 1 (mm)",
        l2: "l2 — Länge des Tangens 2 (mm)",
        r1: "r1 — Biegeradius (Mitte, mm)",
        alpha: "α — Biegewinkel (z. B. 45°, 90°)",
        caption: "Die Bezeichnungen entsprechen den Spalten in den Tabellen unten. Verlängerte Tangenten ermöglichen das direkte Aufsetzen des Orbitalschweißkopfs an der Bogenkante.",
      },
    },
    computeFailed: "Berechnung fehlgeschlagen",
    iso: {
      loading: "Lade Referenztabellen…",
      loadFailed: "Laden fehlgeschlagen",
      bannerTag: "Technische Referenzdaten · eigene kuratierte Sammlung",
      bannerP1Part1: "Werte zusammengestellt aus öffentlich verfügbaren",
      bannerP1Highlight: "Rohrhersteller-Katalogen",
      bannerP1Part2: "(Sandvik, Outokumpu, Mannesmann, Marcegaglia), technischen Handbüchern und traditionellen Handwerks-Tabellen. Bezeichnungen wie „ISO 1127\" werden als gängige Industrie-Kurzform verwendet — diese App",
      bannerP1Reprod: "reproduziert keine",
      bannerP1Part3: "urheberrechtlich geschützten ISO-Dokumente.",
      bannerP2Part1: "Für Zertifizierung, Maßprüfung oder vertragliche Pflichten verwenden Sie immer die Originalnormen von",
      bannerP2Part2: "oder Ihrer nationalen Normungsbehörde (DIN für Deutschland).",
      filterPlaceholder: "Nach OD / DN / NPS filtern…",
      noResults: (q) => `Keine Ergebnisse für „${q}\".`,
      footerDisclaimer: "⚠ Tigik haftet nicht für Abweichungen von offiziellen Normen. Die Werte sind Richtwerte — Werkstatt-Referenz, kein Zertifizierungsdokument.",
      subTabs: {
        iso1127:  { label: "Edelstahl-Rohre",        desc: "Größen & Wandstärken" },
        iso4200:  { label: "Rohrmassen",             desc: "kg/m nach Abmessung" },
        iso2768:  { label: "Allgemeintoleranzen",    desc: "Linear + Winkel" },
        iso6708:  { label: "DN ↔ NPS Umrechner",     desc: "Europa ↔ USA" },
        iso15649: { label: "PN-Druckklassen",        desc: "Petrochemie" },
        iso13480: { label: "Pharma & Food Finish",   desc: "Ra μm, Schweißklassen" },
      },
      title1127:    "Standardmaße Edelstahlrohre (Referenz, vgl. ISO 1127)",
      title4200:    "Massen von Stahlrohren (kg/m, physikalisch berechnet · vgl. ISO 4200)",
      title2768lin: "Allgemeine Linear-Toleranzen (mm · vgl. ISO 2768-1)",
      title2768ang: "Allgemeine Winkel-Toleranzen (vgl. ISO 2768-2)",
      title6708:    "DN ↔ NPS Umrechner (Europa ↔ USA · vgl. ISO 6708)",
      title15649:   "PN-Druckklassen für Petrochemie (vgl. ISO 15649)",
      title13480:   "Oberflächen-Finish für Food & Pharma (Ra μm · vgl. ISO 13480)",
      title13458:   "Schweißklassen für Food / Pharma (vgl. ISO 13458)",
      colOdMm: "OD (mm)", colWallMm: "Wand (mm)", colKgM: "kg / m",
      colRange: "Bereich", colFineLin: "f (fein)", colMedLin: "m (mittel)", colCoarseLin: "c (grob)", colVeryCoarseLin: "v (sehr grob)",
      colShortSide: "Kurze Seite", colHFine: "H (fein)", colKMed: "K (mittel)", colLCoarse: "L (grob)",
      colDn: "DN", colNps: "NPS (Zoll)",
      colPn: "PN (bar)", colAnsiClass: "ANSI Class", colUseCase: "Typische Verwendung",
      colCode: "Code", colRaMax: "Ra max (μm)", colMethod: "Methode", colUse: "Verwendung",
      colClass: "Klasse", colDesc: "Beschreibung", colDefects: "Zulässige Fehler",
    },
    pnUse: {
      6:   "Niederdruck-Leitungen, Wasser, Luft",
      10:  "Standard-Industrieleitungen, Wasser, LP-Gas",
      16:  "Standard für ND-Dampf, neutrale Chemikalien",
      25:  "Prozessleitungen, MD-Dampf, Öl",
      40:  "Petrochemie MD, Öle, aggressive Flüssigkeiten",
      63:  "HD-Prozessleitungen, Dampfverteilung",
      100: "Hochdruck-Prozesse, Hydraulik, HD-Dampf",
      160: "Petrochemie, Raffinerien, überkritischer Dampf",
      250: "Ultra-HD — chemische Synthese, Ölfelder",
      320: "Spezialanwendungen, Nuklearindustrie",
      400: "Ultra-speziell, F&E, Raumfahrt",
    },
    finishMethod: {
      "1D":   "Mechanisch (gebürstet)",
      "2B":   "Kaltgewalzt",
      "2BA":  "Glanzgeglüht & poliert",
      "180G": "Schleifen Korn 180",
      "240G": "Schleifen Korn 240",
      "320G": "Schleifen Korn 320",
      "400G": "Schleifen Korn 400",
      "EP":   "Elektropoliert",
      "EP+":  "EP + Passivierung",
    },
    finishUse: {
      "1D":   "Außenflächen, nicht-aggressive Medien",
      "2B":   "Standard-Pharma, WFI-Wasser",
      "2BA":  "Pharma, Kosmetik, saubere Medien",
      "180G": "Lebensmittel (Molkerei, Brauerei)",
      "240G": "Höhere Lebensmittel-Klasse",
      "320G": "Pharma-Standard",
      "400G": "Hochpharma, Biotech",
      "EP":   "Ultra-Pharma, Halbleiter, Vakuum",
      "EP+":  "Top-Klasse — Bioreaktoren, sterile Injektion",
    },
    weldDesc: {
      "1": "Voll durchgeschweißt mit glatter Decklage",
      "2": "Voll durchgeschweißt mit kleinen Mängeln",
      "3": "Standardnaht mit sichtbarer Wurzelseite",
      "4": "Funktionsnaht ohne kosmetische Anforderungen",
    },
    weldDefects: {
      "1": "Keine (≤ 0,1 mm)",
      "2": "≤ 0,3 mm Porendurchmesser",
      "3": "≤ 0,5 mm",
      "4": "≤ 1,0 mm",
    },
    weldUse: {
      "1": "Pharma WFI, Biotech, sterile Medien",
      "2": "Lebensmittel, Molkereien",
      "3": "Allgemeine Prozessleitungen",
      "4": "Hilfsleitungen, Drainage",
    },
  },

  pl: {
    tag: "RUROCIĄGI",
    title: "Tabele dla rurarzy",
    sub: "Segmenty · Odsadzenia · Długości łuków · Kolana",
    loadingTables: "Wczytuję tabele…",
    disclaimer: "Tabele rurarskie: nie gwarantujemy bezbłędności dla każdego wymiaru ani odchyleń od oryginalnych rysunków. Przed cięciem materiału zawsze sprawdź wartości na próbce.",
    tabs: {
      segment: "Segment / DN × kąt",
      offset:  "Odsadzenie (Offset)",
      arc:     "Łuk (K, F)",
      elbow:   "Kolana DIN / ISO",
      orbital: "Kolana orbitalne",
      iso:     "Tabele referencyjne",
    },
    segmentHelp: "Wartości w mm — długość segmentu dla rury o danej DN i kącie odsadzenia.",
    angleByDn: "Kąt \\ DN",
    formula: "Wzór",
    offsetH: "Odsadzenie h (mm)",
    offsetR: "Promień gięcia r (mm)",
    angleDeg: (a, b) => `Kąt (°) — ${a}–${b}`,
    computeZ: "Oblicz Z",
    computeHL: "Oblicz H i L",
    radius: "Promień R (mm)",
    zLen: "Z (długość segmentu)",
    height: "H (wysokość)",
    arcLen: "L (długość łuku)",
    fCoeffTitle: "Współczynniki f1, f2",
    kfCoeffTitle: "Współczynniki K, F",
    legendTitle: "Co jest co? — wyjaśnienia dla laika",
    legendShow: "Pokaż legendę",
    legendHide: "Ukryj legendę",
    legend: {
      R: "Promień gięcia rury (mm) — od osi rury. Większy = łagodniejszy łuk.",
      H: "Wysokość odsadzenia — prostopadła odległość między oryginalną a nową osią rury (mm).",
      L: "Długość łuku — ile milimetrów rury zużyje gięcie.",
      Z: "Długość segmentu — całkowita długość skośnego kawałka między dwoma kolanami.",
      K: "Współczynnik K = H/R. Liczba bezwymiarowa — relacja łuku do promienia.",
      F: "Kąt w radianach (F = stopnie × π / 180). Używane w L = R·F.",
      angle: "Kąt gięcia w stopniach (najczęściej 45° lub 90°).",
      DN: "Nominalna średnica rury (DN50 ≈ 50 mm, DN100 ≈ 100 mm).",
    },
    elbowCardTitle: (title) => `${title} — kolana 90° / 45° (mm)`,
    orbital: {
      title: "Kolana orbitalne · przedłużony tangens",
      desc: "Przedłużone kolana do spawania orbitalnego higienicznych rur ze stali nierdzewnej. Dłuższy tangens (l1, l2) pozwala na zamocowanie głowicy orbitalnej tuż przy krawędzi kolana. Norma: DIN 11865 (imperial / OD) i DIN 11866 (metric / DN).",
      footnotes: "d1 — średnica zewnętrzna rury (mm) · s1 — grubość ścianki (mm) · l1, l2 — długości odcinków stycznych (mm) · r1 — promień gięcia (mm) · * wersja przedłużona · ** grubość ścianki nie odpowiada DIN 11865/11866 (na życzenie).",
      diagram: {
        title: "Wymiary kolana — legenda",
        d1: "d1 — średnica zewnętrzna rury (mm)",
        s1: "s1 — grubość ścianki (mm)",
        l1: "l1 — długość odcinka stycznego 1 (mm)",
        l2: "l2 — długość odcinka stycznego 2 (mm)",
        r1: "r1 — promień gięcia (środek, mm)",
        alpha: "α — kąt gięcia (np. 45°, 90°)",
        caption: "Oznaczenia odpowiadają kolumnom w tabelach poniżej. Dłuższe odcinki styczne umożliwiają zamocowanie głowicy orbitalnej bezpośrednio przy krawędzi kolana.",
      },
    },
    computeFailed: "Obliczenie nieudane",
    iso: {
      loading: "Wczytuję tabele referencyjne…",
      loadFailed: "Wczytanie nieudane",
      bannerTag: "Referencyjne dane inżynierskie · własna zebrana kolekcja",
      bannerP1Part1: "Wartości zebrane z publicznie dostępnych",
      bannerP1Highlight: "katalogów producentów rur",
      bannerP1Part2: "(Sandvik, Outokumpu, Mannesmann, Marcegaglia), poradników inżynierskich i tradycyjnych tabel rzemieślniczych. Oznaczenia jak „ISO 1127\" stosowane są jako powszechne skróty branżowe — ta aplikacja",
      bannerP1Reprod: "nie reprodukuje",
      bannerP1Part3: "żadnych chronionych prawem autorskim dokumentów ISO.",
      bannerP2Part1: "Do celów certyfikacyjnych, inspekcji wymiarowych lub zobowiązań umownych zawsze używaj oryginalnych norm z",
      bannerP2Part2: "lub krajowego organu (PKN dla Polski).",
      filterPlaceholder: "Filtruj wg OD / DN / NPS…",
      noResults: (q) => `Brak wyników dla „${q}\".`,
      footerDisclaimer: "⚠ Tigik nie ponosi odpowiedzialności za odchylenia od oficjalnych norm. Wartości orientacyjne — referencja warsztatowa, nie dokument certyfikacyjny.",
      subTabs: {
        iso1127:  { label: "Rury nierdzewne",        desc: "Wymiary & grubości ścianek" },
        iso4200:  { label: "Masy rur",                desc: "kg/m wg wymiaru" },
        iso2768:  { label: "Tolerancje swobodne",     desc: "Liniowe + kątowe" },
        iso6708:  { label: "DN ↔ NPS przelicznik",    desc: "Europa ↔ USA" },
        iso15649: { label: "PN klasy ciśnieniowe",    desc: "Petrochemia" },
        iso13480: { label: "Pharma & Food finish",    desc: "Ra μm, klasy spawów" },
      },
      title1127:    "Standardowe wymiary rur nierdzewnych (referencyjne, ref. ISO 1127)",
      title4200:    "Masy rur stalowych (kg/m, obliczone z fizyki · ref. ISO 4200)",
      title2768lin: "Tolerancje liniowe swobodne (mm · ref. ISO 2768-1)",
      title2768ang: "Tolerancje kątowe swobodne (ref. ISO 2768-2)",
      title6708:    "Przelicznik DN ↔ NPS (Europa ↔ USA · ref. ISO 6708)",
      title15649:   "Klasy ciśnieniowe PN dla petrochemii (ref. ISO 15649)",
      title13480:   "Wykończenia powierzchni dla Food & Pharma (Ra μm · ref. ISO 13480)",
      title13458:   "Klasy spawów dla spożywczych / pharma (ref. ISO 13458)",
      colOdMm: "OD (mm)", colWallMm: "Ścianka (mm)", colKgM: "kg / m",
      colRange: "Zakres", colFineLin: "f (drobne)", colMedLin: "m (średnie)", colCoarseLin: "c (zgrubne)", colVeryCoarseLin: "v (bardzo zgrubne)",
      colShortSide: "Krótki bok", colHFine: "H (drobne)", colKMed: "K (średnie)", colLCoarse: "L (zgrubne)",
      colDn: "DN", colNps: "NPS (cale)",
      colPn: "PN (bar)", colAnsiClass: "ANSI Class", colUseCase: "Typowe zastosowanie",
      colCode: "Kod", colRaMax: "Ra max (μm)", colMethod: "Metoda", colUse: "Zastosowanie",
      colClass: "Klasa", colDesc: "Opis", colDefects: "Dopuszczalne wady",
    },
    pnUse: {
      6:   "Niskociśnieniowe instalacje, woda, powietrze",
      10:  "Standardowe instalacje przemysłowe, woda, gaz LP",
      16:  "Standard dla pary NT, chemia neutralna",
      25:  "Rurociągi procesowe, para ST, ropa",
      40:  "Petrochemia ST, oleje, agresywne ciecze",
      63:  "Wysokociśnieniowe rurociągi procesowe, para",
      100: "Procesy WC, hydraulika, para WC",
      160: "Petrochemia, rafinerie, para nadkrytyczna",
      250: "Ultra-WC — synteza chemiczna, pola naftowe",
      320: "Zastosowania specjalne, energetyka jądrowa",
      400: "Ultra-specjalne, B+R, kosmonautyka",
    },
    finishMethod: {
      "1D":   "Mechaniczne (szczotkowanie)",
      "2B":   "Walcowane na zimno",
      "2BA":  "Wyżarzane i polerowane",
      "180G": "Szlifowanie ziarno 180",
      "240G": "Szlifowanie ziarno 240",
      "320G": "Szlifowanie ziarno 320",
      "400G": "Szlifowanie ziarno 400",
      "EP":   "Elektrochemiczne polerowanie",
      "EP+":  "EP + pasywacja",
    },
    finishUse: {
      "1D":   "Powierzchnie zewnętrzne, media nieagresywne",
      "2B":   "Standard pharma, woda WFI",
      "2BA":  "Pharma, kosmetyki, czyste media",
      "180G": "Spożywczy (mleko, browar)",
      "240G": "Wyższa klasa spożywcza",
      "320G": "Standard pharma",
      "400G": "Wysoka pharma, biotech",
      "EP":   "Ultra-pharma, semiconductor, próżnia",
      "EP+":  "Top — bioreaktory, sterylne iniekcje",
    },
    weldDesc: {
      "1": "Pełne przetopienie z gładką licówką",
      "2": "Pełne przetopienie z drobnymi niedoskonałościami",
      "3": "Standardowy spaw z widoczną stroną graniową",
      "4": "Funkcjonalny spaw bez wymagań kosmetycznych",
    },
    weldDefects: {
      "1": "Brak (≤ 0,1 mm)",
      "2": "≤ 0,3 mm średnica porów",
      "3": "≤ 0,5 mm",
      "4": "≤ 1,0 mm",
    },
    weldUse: {
      "1": "Pharma WFI, biotech, media sterylne",
      "2": "Spożywcze, mleczarnie",
      "3": "Zwykłe rurociągi procesowe",
      "4": "Instalacje pomocnicze, drenaż",
    },
  },
  fr: {
    tag: "TUYAUTEURS",
    title: "Tables pour tuyauteurs",
    sub: "Segments · Décalages · Longueurs d'arc · Coudes",
    loadingTables: "Chargement des tables…",
    disclaimer: "Tables tuyauterie : on ne garantit pas l'exactitude pour chaque dimension ni les éventuels écarts par rapport aux plans d'origine. Vérifie toujours les valeurs sur une pièce témoin avant de couper le matériau.",
    tabs: {
      segment: "Segment / DN × angle",
      offset:  "Décalage (Offset)",
      arc:     "Arc (K, F)",
      elbow:   "Coudes DIN / ISO",
      orbital: "Coudes orbitaux",
      iso:     "Tables de référence",
    },
    segmentHelp: "Valeurs en mm — longueur de segment pour un tube DN donné et un angle de décalage.",
    angleByDn: "Angle \\ DN",
    formula: "Formule",
    offsetH: "Décalage h (mm)",
    offsetR: "Rayon de cintrage r (mm)",
    angleDeg: (a, b) => `Angle (°) — ${a}–${b}`,
    computeZ: "Calculer Z",
    computeHL: "Calculer H et L",
    radius: "Rayon R (mm)",
    zLen: "Z (longueur de segment)",
    height: "H (hauteur)",
    arcLen: "L (longueur d'arc)",
    fCoeffTitle: "Coefficients f1, f2",
    kfCoeffTitle: "Coefficients K, F",
    legendTitle: "C'est quoi ? — explications pour débutant",
    legendShow: "Afficher les explications",
    legendHide: "Masquer les explications",
    legend: {
      R: "Rayon de cintrage du tube (mm) — depuis l'axe du tube. Plus il est grand, plus la courbe est « douce ».",
      H: "Hauteur du décalage (offset) — distance perpendiculaire entre l'axe d'origine et le nouvel axe du tube (mm).",
      L: "Longueur d'arc — combien de millimètres de tube tu consommes pour le cintrage.",
      Z: "Longueur de segment — longueur totale de la pièce inclinée entre deux coudes.",
      K: "Coefficient K = H/R. Nombre sans dimension, indique la taille de l'arc par rapport au rayon.",
      F: "Angle en radians (F = degrés × π / 180). Utilisé dans le calcul L = R·F.",
      angle: "Angle de cintrage en degrés (le plus souvent 45° ou 90°).",
      DN: "Diamètre nominal du tube (DN50 = environ 50 mm, DN100 = 100 mm).",
    },
    /* ISO ref tables continuent ci-dessous */
    elbowCardTitle: (title) => `${title} — coudes 90° / 45° (mm)`,
    orbital: {
      title: "Coudes orbitaux · tangente prolongée",
      desc: "Coudes à tangente prolongée pour soudage orbital de tubes inox sanitaires. Tangente plus longue (l1, l2) permet de fixer la tête de soudage orbitale juste au bord du coude. Norme DIN 11865 (impérial / OD) et DIN 11866 (métrique / DN).",
      footnotes: "d1 — diamètre extérieur du tube (mm) · s1 — épaisseur de paroi (mm) · l1, l2 — longueurs des parties droites tangentielles (mm) · r1 — rayon de cintrage (mm) · * version prolongée · ** épaisseur de paroi non conforme DIN 11865/11866 (sur demande).",
      diagram: {
        title: "Dimensions du coude — explications",
        d1: "d1 — diamètre extérieur du tube (mm)",
        s1: "s1 — épaisseur de paroi (mm)",
        l1: "l1 — longueur de la partie tangentielle 1 (mm)",
        l2: "l2 — longueur de la partie tangentielle 2 (mm)",
        r1: "r1 — rayon de cintrage (centre, mm)",
        alpha: "α — angle de cintrage (ex. 45°, 90°)",
        caption: "Les désignations correspondent aux colonnes des tables ci-dessous. Les parties tangentielles plus longues (l1, l2) permettent de fixer la tête de soudage orbitale directement au bord du coude.",
      },
    },
    computeFailed: "Calcul échoué",
    /* ISO référence tables */
    iso: {
      loading: "Chargement des tables de référence…",
      loadFailed: "Échec du chargement",
      bannerTag: "Données d'ingénierie de référence · collection maison",
      bannerP1Part1: "Valeurs compilées depuis des",
      bannerP1Highlight: "catalogues fabricants de tubes",
      bannerP1Part2: "accessibles publiquement (Sandvik, Outokumpu, Mannesmann, Marcegaglia), des manuels d'ingénierie et des tables d'atelier traditionnelles. Les désignations comme « ISO 1127 » sont utilisées comme repères industriels courants — cette appli",
      bannerP1Reprod: "ne reproduit pas",
      bannerP1Part3: "de documents ISO sous copyright.",
      bannerP2Part1: "Pour certification, contrôle dimensionnel ou engagements contractuels, utilise toujours les normes originales depuis",
      bannerP2Part2: "ou l'organisme national (ÚNMS SR pour la Slovaquie).",
      filterPlaceholder: "Filtrer par OD / DN / NPS…",
      noResults: (q) => `Aucun résultat pour « ${q} ».`,
      footerDisclaimer: "⚠ Tigik n'est pas responsable d'éventuels écarts par rapport aux normes officielles. Les valeurs sont indicatives et servent d'aide d'atelier — pas de document de certification.",
      subTabs: {
        iso1127:  { label: "Tubes inox",           desc: "Dimensions & épaisseurs" },
        iso4200:  { label: "Masses tubes",         desc: "kg/m selon dimension" },
        iso2768:  { label: "Tolérances libres",    desc: "Linéaires + angulaires" },
        iso6708:  { label: "Convertisseur DN ↔ NPS", desc: "Europe ↔ USA" },
        iso15649: { label: "Classes PN pression",  desc: "Pétrochimie" },
        iso13480: { label: "Pharma & Food finish", desc: "Ra μm, classes soudures" },
      },
      title1127:    "Dimensions standard tubes inox (référence, réf. ISO 1127)",
      title4200:    "Masses tubes acier carbone (kg/m, calculées physique · réf. ISO 4200)",
      title2768lin: "Tolérances linéaires libres (mm · réf. ISO 2768-1)",
      title2768ang: "Tolérances angulaires libres (réf. ISO 2768-2)",
      title6708:    "Convertisseur DN ↔ NPS (Europe ↔ USA · réf. ISO 6708)",
      title15649:   "Classes PN pression pour pétrochimie (réf. ISO 15649)",
      title13480:   "Finitions surfaces Food & Pharma (Ra μm · réf. ISO 13480)",
      title13458:   "Classes de soudures pour agroalimentaire / pharma (réf. ISO 13458)",
      colOdMm: "OD (mm)", colWallMm: "Paroi (mm)", colKgM: "kg / m",
      colRange: "Plage", colFineLin: "f (fin)", colMedLin: "m (moyen)", colCoarseLin: "c (gros)", colVeryCoarseLin: "v (très gros)",
      colShortSide: "Côté court", colHFine: "H (fin)", colKMed: "K (moyen)", colLCoarse: "L (gros)",
      colDn: "DN", colNps: "NPS (pouces)",
      colPn: "PN (bar)", colAnsiClass: "ANSI Class", colUseCase: "Usage typique",
      colCode: "Code", colRaMax: "Ra max (μm)", colMethod: "Méthode", colUse: "Usage",
      colClass: "Classe", colDesc: "Description", colDefects: "Défauts autorisés",
    },
    /* Lookup texts for backend-returned codes (PN, finish-code, weld-class) */
    pnUse: {
      6:   "Basse pression, eau, air",
      10:  "Circuits industriels standards, eau, gaz BP",
      16:  "Standard vapeur BP, fluides non agressifs",
      25:  "Tuyauteries process, vapeur MP, pétrole",
      40:  "Pétrochimie MP, huiles, liquides agressifs",
      63:  "Process HP, circuits vapeur",
      100: "Process haute pression, hydraulique, vapeur HP",
      160: "Pétrochimie, raffineries, vapeur supercritique",
      250: "Ultra-HP — synthèse chimique, champs pétroliers",
      320: "Applications spéciales, industrie nucléaire",
      400: "Ultra-spécial, R&D, industrie spatiale",
    },
    finishMethod: {
      "1D":   "Mécanique (brossage)",
      "2B":   "Laminé à froid",
      "2BA":  "Recuit et poli",
      "180G": "Grain 180",
      "240G": "Grain 240",
      "320G": "Grain 320",
      "400G": "Grain 400",
      "EP":   "Polissage électrolytique",
      "EP+":  "EP + passivation",
    },
    finishUse: {
      "1D":   "Surfaces externes, fluide non agressif",
      "2B":   "Pharma standard, eau WFI",
      "2BA":  "Pharma, cosmétique, fluides propres",
      "180G": "Agroalimentaire (lait, bière)",
      "240G": "Agroalimentaire haut de gamme",
      "320G": "Pharma standard",
      "400G": "Pharma haute, biotech",
      "EP":   "Ultra-pharma, semi-conducteur, vide",
      "EP+":  "Top — bioréacteurs, injections stériles",
    },
    weldDesc: {
      "1": "Pleine pénétration, surface supérieure lisse",
      "2": "Pleine pénétration, légères imperfections",
      "3": "Soudure standard, côté racine visible",
      "4": "Soudure fonctionnelle, pas d'exigence esthétique",
    },
    weldDefects: {
      "1": "Aucun (≤ 0,1 mm)",
      "2": "≤ 0,3 mm diamètre pores",
      "3": "≤ 0,5 mm",
      "4": "≤ 1,0 mm",
    },
    weldUse: {
      "1": "Pharma WFI, biotech, fluides stériles",
      "2": "Agroalimentaire, laiteries",
      "3": "Tuyauteries process courantes",
      "4": "Circuits auxiliaires, drainage",
    },
  },
};

export default function Pipefitter() {
  const { user, loading } = useAuth();
  const { lang } = useLang();
  const l = L[lang] || L.sk;
  const [tables, setTables] = useState(null);
  const [tab, setTab] = useState("segment");

  useEffect(() => {
    if (!user) return;
    api.get("/pipefitter/tables").then((r) => setTables(r.data)).catch(() => {});
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!tables) return <div className="max-w-7xl mx-auto px-6 py-10 text-zinc-500 font-mono">{l.loadingTables}</div>;

  const TABS = [
    { id: "segment", label: l.tabs.segment },
    { id: "offset",  label: l.tabs.offset },
    { id: "arc",     label: l.tabs.arc },
    { id: "elbow",   label: l.tabs.elbow },
    { id: "orbital", label: l.tabs.orbital },
    { id: "iso",     label: l.tabs.iso },
  ];

  return (
    <div data-testid="pipefitter-page" className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="mb-6">
        <span className="wb-tag !border-gold-600 !text-gold-500">{l.tag}</span>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-2">{l.title}</h1>
        <p className="text-sm text-zinc-500 font-mono uppercase tracking-wider mt-2">{l.sub}</p>
      </div>

      {!tables.pro && (
        <div className="mb-6 max-w-2xl">
          <ProGate compact message={tables.preview_message} />
        </div>
      )}

      <div className="border-b border-zinc-800 mb-6 flex gap-1 overflow-x-auto">
        {TABS.map((tt) => (
          <button key={tt.id} data-testid={`pipefitter-tab-${tt.id}`} onClick={() => setTab(tt.id)}
            className={`px-3 py-2.5 text-xs font-mono uppercase tracking-wider border-b-2 whitespace-nowrap ${tab === tt.id ? "border-gold-500 text-gold-400" : "border-transparent text-zinc-500 hover:text-white"}`}>
            {tt.label}
          </button>
        ))}
      </div>

      {tab === "segment" && <SegmentTab tables={tables} l={l} />}
      {tab === "offset"  && <OffsetTab f_table={tables.offset_f_table} l={l} />}
      {tab === "arc"     && <ArcTab kf_table={tables.kf_arc_table} l={l} />}
      {tab === "elbow"   && <ElbowTab din={tables.elbow_table_din} iso={tables.elbow_table_iso} l={l} />}
      {tab === "orbital" && <OrbitalElbowTab l={l} />}
      {tab === "iso"     && <IsoTab l={l} />}

      <div className="mt-8"><CalcDisclaimer extra={l.disclaimer} /></div>
    </div>
  );
}

/* --- Segment lookup (DN × uhol) --- */
function SegmentTab({ tables, l }) {
  const dns = tables.segment_dn_columns;
  const rows = Object.entries(tables.segment_table).map(([angle, vals]) => ({ angle: Number(angle), vals }));
  rows.sort((a, b) => a.angle - b.angle);
  return (
    <div data-testid="segment-table">
      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-2 py-2 text-left">{l.angleByDn}</th>
              {dns.map((d) => <th key={d} className="px-2 py-2 text-right">DN{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.angle} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                <td className="px-2 py-1.5 font-display font-bold text-gold-400">{r.angle}°</td>
                {r.vals.map((v, i) => (
                  <td key={i} className="px-2 py-1.5 text-right font-mono">{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500 mt-2 font-mono">{l.segmentHelp}</p>
      <div className="mt-4">
        <PipefitterLegend l={l} symbols={["angle", "DN", "Z"]} />
      </div>
    </div>
  );
}

/* --- Offset (Z = h*f1 - r*f2) --- */
function OffsetTab({ f_table, l }) {
  const [h, setH] = useState(500);
  const [r, setR] = useState(205);
  const [a, setA] = useState(45);
  const [out, setOut] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const compute = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(""); setOut(null);
    try {
      const r2 = await api.post("/pipefitter/offset", { h: parseFloat(h), r: parseFloat(r), angle_deg: parseInt(a, 10) });
      setOut(r2.data);
    } catch (e2) { setErr(e2.response?.data?.detail || l.computeFailed); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="offset-tab">
      <div className="lg:col-span-5 space-y-4">
      <form onSubmit={compute} className="border border-zinc-800 bg-[var(--wb-surface)] p-6">
        <div className="text-xs font-mono uppercase tracking-wider text-gold-500 mb-3">{l.formula}: Z = (h × f1) − (r × f2)</div>
        <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1 mt-3">{l.offsetH}</label>
        <input data-testid="offset-h" type="number" value={h} onChange={(e) => setH(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" />
        <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1 mt-3">{l.offsetR}</label>
        <input data-testid="offset-r" type="number" value={r} onChange={(e) => setR(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" />
        <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1 mt-3">{l.angleDeg(1, 90)}</label>
        <input data-testid="offset-a" type="number" min="1" max="90" value={a} onChange={(e) => setA(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" />
        <button type="submit" disabled={busy} className="wb-btn-primary w-full py-3 mt-5 text-sm inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}<Calculator className="w-4 h-4" /> {l.computeZ}
        </button>
        {err && (err.startsWith("PRO_REQUIRED:")
          ? <div className="mt-3"><ProGate compact message={err.replace("PRO_REQUIRED:", "").trim()} /></div>
          : <div className="mt-3 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{err}</div>
        )}
      </form>
      <PipefitterLegend l={l} symbols={["H", "R", "Z", "angle"]} />
      </div>
      <div className="lg:col-span-7">
        {out ? (
          <div className="space-y-px bg-zinc-800">
            <div className="bg-[var(--wb-surface)] p-6">
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{l.zLen}</div>
              <div className="font-display font-black text-5xl text-white">{out.z_mm} <span className="text-lg font-mono text-zinc-500">mm</span></div>
            </div>
            <div className="grid grid-cols-2 gap-px">
              <Cell label="f1" v={out.f1} />
              <Cell label="f2" v={out.f2} />
            </div>
            <div className="bg-[var(--wb-surface)] p-4 text-xs text-zinc-500 font-mono">{out.formula}</div>
          </div>
        ) : (
          <FCoeffTable f_table={f_table} title={l.fCoeffTitle} />
        )}
      </div>
    </div>
  );
}

function FCoeffTable({ f_table, title }) {
  const rows = Object.entries(f_table).map(([k, [f1, f2]]) => ({ deg: Number(k), f1, f2 })).sort((a, b) => a.deg - b.deg);
  return (
    <div className="border border-zinc-800 overflow-x-auto max-h-[480px]">
      <div className="px-3 py-2 bg-zinc-900 text-xs font-mono uppercase text-zinc-400 sticky top-0">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/60 text-xs font-mono uppercase text-zinc-500">
          <tr><th className="px-3 py-2 text-left">°</th><th className="px-3 py-2 text-right">f1</th><th className="px-3 py-2 text-right">f2</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.deg} className="border-t border-zinc-800">
              <td className="px-3 py-1.5 font-display font-bold text-gold-400">{r.deg}°</td>
              <td className="px-3 py-1.5 text-right font-mono">{r.f1}</td>
              <td className="px-3 py-1.5 text-right font-mono">{r.f2}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArcTab({ kf_table, l }) {
  const [a, setA] = useState(45);
  const [r, setR] = useState(100);
  const [out, setOut] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const compute = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(""); setOut(null);
    try {
      const r2 = await api.post("/pipefitter/arc", { angle_deg: parseInt(a, 10), radius: parseFloat(r) });
      setOut(r2.data);
    } catch (e2) { setErr(e2.response?.data?.detail || l.computeFailed); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 space-y-4">
      <form onSubmit={compute} className="border border-zinc-800 bg-[var(--wb-surface)] p-6">
        <div className="text-xs font-mono uppercase tracking-wider text-gold-500 mb-3">K = H/R · H = R·K · L = R·F</div>
        <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{l.radius}</label>
        <input type="number" value={r} onChange={(e) => setR(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" />
        <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1 mt-3">{l.angleDeg(0, 90)}</label>
        <input type="number" min="0" max="90" value={a} onChange={(e) => setA(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" />
        <button type="submit" disabled={busy} className="wb-btn-primary w-full py-3 mt-5 text-sm inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}<Calculator className="w-4 h-4" /> {l.computeHL}
        </button>
        {err && (err.startsWith("PRO_REQUIRED:")
          ? <div className="mt-3"><ProGate compact message={err.replace("PRO_REQUIRED:", "").trim()} /></div>
          : <div className="mt-3 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm"><AlertCircle className="w-4 h-4 inline mr-1" />{err}</div>
        )}
      </form>
      <PipefitterLegend l={l} symbols={["R", "H", "L", "K", "F", "angle"]} />
      </div>
      <div className="lg:col-span-7">
        {out ? (
          <div className="space-y-px bg-zinc-800">
            <div className="grid grid-cols-2 gap-px">
              <div className="bg-[var(--wb-surface)] p-5">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{l.height}</div>
                <div className="font-display font-black text-4xl">{out.H_mm} <span className="text-sm font-mono text-zinc-500">mm</span></div>
              </div>
              <div className="bg-[var(--wb-surface)] p-5">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{l.arcLen}</div>
                <div className="font-display font-black text-4xl">{out.L_mm} <span className="text-sm font-mono text-zinc-500">mm</span></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-px"><Cell label="K" v={out.K} /><Cell label="F" v={out.F} /></div>
          </div>
        ) : <KFTable kf={kf_table} title={l.kfCoeffTitle} />}
      </div>
    </div>
  );
}

function KFTable({ kf, title }) {
  const rows = Object.entries(kf).map(([k, [K, F]]) => ({ deg: Number(k), K, F })).sort((a, b) => a.deg - b.deg);
  return (
    <div className="border border-zinc-800 overflow-x-auto max-h-[480px]">
      <div className="px-3 py-2 bg-zinc-900 text-xs font-mono uppercase text-zinc-400 sticky top-0">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/60 text-xs font-mono uppercase text-zinc-500">
          <tr><th className="px-3 py-2 text-left">°</th><th className="px-3 py-2 text-right">K</th><th className="px-3 py-2 text-right">F</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.deg} className="border-t border-zinc-800">
              <td className="px-3 py-1.5 font-display font-bold text-gold-400">{r.deg}°</td>
              <td className="px-3 py-1.5 text-right font-mono">{r.K}</td>
              <td className="px-3 py-1.5 text-right font-mono">{r.F}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- PipefitterLegend ----------
 * Visual diagram + plain-language explanation of R, H, L, Z, K, F, angle, DN.
 * Collapsible so it doesn't crowd the calculator UI.
 * Used in: ArcTab, OffsetTab, SegmentTab.
 */
function PipefitterLegend({ l, defaultOpen = false, symbols = ["R", "H", "L", "K", "F", "angle"] }) {
  const [open, setOpen] = useState(defaultOpen);
  const lg = l.legend || {};
  return (
    <div className="border border-zinc-800 bg-[var(--wb-surface)]" data-testid="pipefitter-legend">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-zinc-900/40 transition-colors"
        data-testid="pipefitter-legend-toggle"
      >
        <span className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-gold-400" />
          <span className="text-xs font-mono uppercase tracking-wider text-gold-400">
            {l.legendTitle || "What's what?"}
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="border-t border-zinc-800 p-5 space-y-5">
          {/* SVG diagram showing the pipe offset with labeled R, H, L, angle */}
          <div className="flex justify-center bg-zinc-950/60 border border-zinc-800 p-3">
            <svg viewBox="0 0 420 230" className="w-full max-w-[420px] h-auto" aria-hidden="true">
              {/* Original horizontal pipe axis (dashed grey) */}
              <line x1="10" y1="180" x2="410" y2="180" stroke="#52525b" strokeWidth="1" strokeDasharray="4 4" />
              {/* Offset destination horizontal pipe axis (dashed grey) */}
              <line x1="10" y1="60" x2="410" y2="60" stroke="#52525b" strokeWidth="1" strokeDasharray="4 4" />
              {/* Pipe path: in (horizontal) → arc up → out (horizontal) */}
              <path
                d="M 30 180 L 130 180 A 60 60 0 0 1 190 120 L 230 120 A 60 60 0 0 0 290 60 L 390 60"
                fill="none"
                stroke="#f5b800"
                strokeWidth="6"
                strokeLinecap="round"
              />
              {/* H dimension arrow (vertical, between axes) */}
              <line x1="395" y1="60" x2="395" y2="180" stroke="#10b981" strokeWidth="1.5" />
              <polygon points="392,65 398,65 395,58" fill="#10b981" />
              <polygon points="392,175 398,175 395,182" fill="#10b981" />
              <text x="402" y="125" fill="#10b981" fontSize="14" fontWeight="700" fontFamily="monospace">H</text>
              {/* R radius arrow (from center of first arc to the curve) */}
              <line x1="130" y1="120" x2="170" y2="155" stroke="#60a5fa" strokeWidth="1.5" />
              <text x="135" y="115" fill="#60a5fa" fontSize="14" fontWeight="700" fontFamily="monospace">R</text>
              {/* L arc label */}
              <text x="155" y="125" fill="#f5b800" fontSize="13" fontWeight="700" fontFamily="monospace">L</text>
              {/* angle label */}
              <text x="180" y="170" fill="#a78bfa" fontSize="12" fontWeight="700" fontFamily="monospace">α°</text>
              {/* Legend caption */}
              <text x="30" y="200" fill="#71717a" fontSize="10" fontFamily="monospace">Os pôvodnej rúry</text>
              <text x="30" y="50" fill="#71717a" fontSize="10" fontFamily="monospace">Os novej rúry (po skoku)</text>
            </svg>
          </div>
          {/* Symbol explanations */}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-xs">
            {symbols.includes("R") && (
              <div className="flex gap-2"><dt className="font-display font-black text-blue-400 w-8 text-base">R</dt><dd className="text-zinc-300 leading-snug">{lg.R}</dd></div>
            )}
            {symbols.includes("H") && (
              <div className="flex gap-2"><dt className="font-display font-black text-emerald-400 w-8 text-base">H</dt><dd className="text-zinc-300 leading-snug">{lg.H}</dd></div>
            )}
            {symbols.includes("L") && (
              <div className="flex gap-2"><dt className="font-display font-black text-gold-400 w-8 text-base">L</dt><dd className="text-zinc-300 leading-snug">{lg.L}</dd></div>
            )}
            {symbols.includes("Z") && (
              <div className="flex gap-2"><dt className="font-display font-black text-orange-400 w-8 text-base">Z</dt><dd className="text-zinc-300 leading-snug">{lg.Z}</dd></div>
            )}
            {symbols.includes("K") && (
              <div className="flex gap-2"><dt className="font-display font-black text-gold-300 w-8 text-base">K</dt><dd className="text-zinc-300 leading-snug">{lg.K}</dd></div>
            )}
            {symbols.includes("F") && (
              <div className="flex gap-2"><dt className="font-display font-black text-gold-300 w-8 text-base">F</dt><dd className="text-zinc-300 leading-snug">{lg.F}</dd></div>
            )}
            {symbols.includes("angle") && (
              <div className="flex gap-2"><dt className="font-display font-black text-violet-400 w-8 text-base">α°</dt><dd className="text-zinc-300 leading-snug">{lg.angle}</dd></div>
            )}
            {symbols.includes("DN") && (
              <div className="flex gap-2"><dt className="font-display font-black text-zinc-200 w-10 text-sm">DN</dt><dd className="text-zinc-300 leading-snug">{lg.DN}</dd></div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}

function ElbowTab({ din, iso, l }) {
  return (
    <div className="space-y-4" data-testid="elbow-tab">
      <ElbowDiagram
        labels={(l.orbital && l.orbital.diagram) || undefined}
      />
      <div className="grid md:grid-cols-2 gap-4">
        <ElbowCard title="DIN" data={din} l={l} />
        <ElbowCard title="ISO" data={iso} l={l} />
      </div>
    </div>
  );
}
function ElbowCard({ title, data, l }) {
  const rows = Object.entries(data).map(([dn, v]) => ({ dn: Number(dn), ...v })).sort((a, b) => a.dn - b.dn);
  return (
    <div className="border border-zinc-800">
      <div className="px-3 py-2 bg-zinc-900 text-xs font-mono uppercase text-gold-400">{l.elbowCardTitle(title)}</div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/60 text-xs font-mono uppercase text-zinc-500">
          <tr><th className="px-3 py-2 text-left">DN</th><th className="px-3 py-2 text-right">90°</th><th className="px-3 py-2 text-right">45°</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.dn} className="border-t border-zinc-800">
              <td className="px-3 py-1.5 font-display font-bold">DN{r.dn}</td>
              <td className="px-3 py-1.5 text-right font-mono">{r.deg90}</td>
              <td className="px-3 py-1.5 text-right font-mono">{r.deg45}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({ label, v }) {
  return (
    <div className="bg-[var(--wb-surface)] p-4">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-display font-bold text-xl text-white">{v}</div>
    </div>
  );
}

/* ============================================================
   Orbital-elbow tab — extended-tangent hygienic elbows for
   orbital welding (DIN 11865 imperial / DIN 11866 metric).
   45° + 90° variants with pipe OD (d1), wall (s1), tangent
   lengths (l1/l2) and bend radius (r1). All values in mm.
   Data source: /app/frontend/src/data/orbitalElbows.js
   ============================================================ */
function OrbitalElbowTab({ l }) {
  const [angle, setAngle] = React.useState("45");
  const t = l.orbital || {};
  const diagLabels = t.diagram || {
    title: "Rozmery kolena",
    d1: "d1 — vonkajší priemer rúry (mm)",
    s1: "s1 — hrúbka steny (mm)",
    l1: "l1 — dĺžka tangenciálneho úseku 1 (mm)",
    l2: "l2 — dĺžka tangenciálneho úseku 2 (mm)",
    r1: "r1 — polomer ohybu (mm)",
    caption: "Rozmery zodpovedajú stĺpcom v tabuľke nižšie. l1/l2 = dĺžky rovných tangenciálnych úsekov, ktoré umožňujú uchytenie orbitálnej hlavice.",
  };
  return (
    <div className="space-y-4" data-testid="orbital-elbow-tab">
      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-4">
        <div className="text-xs font-mono uppercase text-gold-400 tracking-wider mb-2">{t.title || "Orbitálne kolená · predĺžený tangens"}</div>
        <p className="text-xs text-zinc-400 leading-relaxed">
          {t.desc || "Predĺžené kolená pre orbitálne zváranie hygienických nerezových rúr. Dlhší tangens (l1, l2) umožňuje uchytenie orbitálnej zváracej hlavice tesne pri hrane kolena. Norma DIN 11865 (imperial / OD) a DIN 11866 (metric / DN)."}
        </p>
      </div>

      {/* Schematic diagram of an elbow with dimension labels */}
      <ElbowDiagram labels={diagLabels} />

      {/* 45° / 90° toggle */}
      <div className="inline-flex border border-zinc-800 w-full md:w-auto">
        {[
          { id: "45", label: "45°" },
          { id: "90", label: "90°" },
        ].map((opt) => (
          <button
            key={opt.id}
            data-testid={`orbital-angle-${opt.id}`}
            onClick={() => setAngle(opt.id)}
            className={`flex-1 md:flex-none px-6 md:px-4 py-3 md:py-2 text-sm md:text-xs font-mono uppercase tracking-wider ${
              angle === opt.id ? "bg-gold-500/15 text-gold-400 border-b-2 border-gold-500" : "text-zinc-500 hover:text-white"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <OrbitalElbowCard
          title="Imperial · DIN 11865"
          data={angle === "45" ? oe45imp : oe90imp}
          angle={angle}
          l={l}
        />
        <OrbitalElbowCard
          title="Metric · DIN 11866"
          data={angle === "45" ? oe45met : oe90met}
          angle={angle}
          l={l}
        />
      </div>
      <div className="text-[11px] text-zinc-500 font-mono leading-relaxed">
        {t.footnotes ||
          "d1 — vonkajší priemer rúry (mm) · s1 — hrúbka steny (mm) · l1, l2 — dĺžky tangenciálnych rovných úsekov (mm) · r1 — polomer ohybu (mm) · * predĺžená verzia · ** hrúbka steny nezodpovedá DIN 11865/11866 (na požiadanie)."}
      </div>
    </div>
  );
}

function OrbitalElbowCard({ title, data, angle }) {
  const is90 = angle === "90";
  return (
    <div className="border border-zinc-800" data-testid={`orbital-card-${title.split(' ')[0].toLowerCase()}-${angle}`}>
      <div className="px-2 md:px-3 py-2 bg-zinc-900 text-[10px] md:text-xs font-mono uppercase text-gold-400 flex items-center justify-between flex-wrap gap-1">
        <span>{title} · {angle}°</span>
        <span className="text-zinc-500 normal-case tracking-normal">{data.length} rozmerov</span>
      </div>
      <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-full text-[11px] md:text-xs">
          <thead className="bg-zinc-900/60 text-[9px] md:text-[10px] font-mono uppercase text-zinc-500 sticky top-0">
            <tr>
              <th className="px-1.5 md:px-2 py-1.5 md:py-2 text-left">Size</th>
              <th className="px-1.5 md:px-2 py-1.5 md:py-2 text-right">d1</th>
              <th className="px-1.5 md:px-2 py-1.5 md:py-2 text-right">s1</th>
              {is90 && <th className="px-1.5 md:px-2 py-1.5 md:py-2 text-right">l1</th>}
              {(!is90 || (data[0]?.l2 !== undefined)) && <th className="px-1.5 md:px-2 py-1.5 md:py-2 text-right">l2</th>}
              {data[0]?.r1 !== undefined && <th className="px-1.5 md:px-2 py-1.5 md:py-2 text-right">r1</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => (
              <tr key={`${r.size}-${i}`} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                <td className="px-1.5 md:px-2 py-1 md:py-1.5 font-display font-bold text-white whitespace-nowrap">
                  {r.size}{r.note ? <sup className="text-amber-400 ml-0.5">{r.note}</sup> : null}
                </td>
                <td className="px-1.5 md:px-2 py-1 md:py-1.5 text-right font-mono">{r.d1.toFixed(2)}</td>
                <td className="px-1.5 md:px-2 py-1 md:py-1.5 text-right font-mono">{r.s1.toFixed(2)}</td>
                {is90 && <td className="px-1.5 md:px-2 py-1 md:py-1.5 text-right font-mono">{r.l1 !== undefined ? r.l1.toFixed(2) : "—"}</td>}
                {(!is90 || (data[0]?.l2 !== undefined)) && <td className="px-1.5 md:px-2 py-1 md:py-1.5 text-right font-mono">{r.l2 !== undefined ? r.l2.toFixed(2) : "—"}</td>}
                {data[0]?.r1 !== undefined && <td className="px-1.5 md:px-2 py-1 md:py-1.5 text-right font-mono">{r.r1 !== undefined ? r.r1.toFixed(2) : "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================
   ISO Tables tab — references for stainless tubes, tolerances,
   DN↔NPS conversion, PN classes, pharma/food finish grades.
   ============================================================ */

function IsoTab({ l }) {
  const [iso, setIso] = useState(null);
  const [sub, setSub] = useState("iso1127");
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/pipefitter/iso-tables")
      .then((r) => setIso(r.data))
      .catch((e) => setErr(e.response?.data?.detail || l.iso.loadFailed));
  }, [l.iso.loadFailed]);

  if (err) return (
    <div className="p-3 border border-red-900 bg-red-950/30 text-red-400 text-sm flex items-center gap-2">
      <AlertCircle className="w-4 h-4" /> {err}
    </div>
  );
  if (!iso) return <div className="text-zinc-500 font-mono text-sm">{l.iso.loading}</div>;

  const ISO_SUB_TABS = [
    { id: "iso1127",  short: "1127",   ...l.iso.subTabs.iso1127 },
    { id: "iso4200",  short: "4200",   ...l.iso.subTabs.iso4200 },
    { id: "iso2768",  short: "2768",   ...l.iso.subTabs.iso2768 },
    { id: "iso6708",  short: "6708",   ...l.iso.subTabs.iso6708 },
    { id: "iso15649", short: "15649",  ...l.iso.subTabs.iso15649 },
    { id: "iso13480", short: "13480",  ...l.iso.subTabs.iso13480 },
  ];

  return (
    <div data-testid="iso-tab" className="space-y-5">
      {!iso.pro && (
        <ProGate compact message={iso.preview_message} />
      )}

      {/* Legal-safe attribution banner */}
      <div className="border border-gold-700/30 bg-gradient-to-br from-zinc-950 to-gold-950/10 p-4">
        <div className="flex items-start gap-2.5">
          <BookOpen className="w-4 h-4 text-gold-500 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] leading-relaxed text-zinc-400">
            <p className="text-gold-300 font-mono uppercase tracking-wider text-[10px] mb-1.5 font-bold">
              {l.iso.bannerTag}
            </p>
            <p>
              {l.iso.bannerP1Part1} <span className="text-zinc-300">{l.iso.bannerP1Highlight}</span>
              {" "}{l.iso.bannerP1Part2} <span className="text-gold-300 font-semibold">{l.iso.bannerP1Reprod}</span>
              {" "}{l.iso.bannerP1Part3}
            </p>
            <p className="mt-1.5">
              {l.iso.bannerP2Part1} <a href="https://www.iso.org" target="_blank" rel="noopener noreferrer" className="text-gold-400 underline hover:text-gold-300">iso.org</a> {l.iso.bannerP2Part2}
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tab selector */}
      <div className="flex flex-wrap gap-2">
        {ISO_SUB_TABS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            data-testid={`iso-subtab-${s.id}`}
            className={`px-3 py-2 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
              sub === s.id
                ? "border-gold-500 text-gold-300 bg-gold-950/30"
                : "border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
            }`}
          >
            <span className="font-bold">{s.label}</span>
            <span className="hidden sm:inline text-zinc-600 ml-1.5 normal-case tracking-normal">· ref. ISO {s.short}</span>
          </button>
        ))}
      </div>

      {/* Universal filter input */}
      {["iso1127", "iso4200", "iso6708"].includes(sub) && (
        <input
          type="text"
          placeholder={l.iso.filterPlaceholder}
          value={filter}
          onChange={(e) => setFilter(e.target.value.toLowerCase())}
          className="wb-input w-full sm:w-72 px-3 py-2 text-sm"
          data-testid="iso-filter-input"
        />
      )}

      {sub === "iso1127"  && <Iso1127Table  rows={iso.iso_1127_tubes} filter={filter} l={l} />}
      {sub === "iso4200"  && <Iso4200Table  rows={iso.iso_4200_tubes} filter={filter} l={l} />}
      {sub === "iso2768"  && <Iso2768Tables linear={iso.iso_2768_linear} angular={iso.iso_2768_angular} l={l} />}
      {sub === "iso6708"  && <Iso6708Table  rows={iso.iso_6708_dn_nps} filter={filter} l={l} />}
      {sub === "iso15649" && <Iso15649Table rows={iso.iso_15649_pn} l={l} />}
      {sub === "iso13480" && <Iso13480Tables finish={iso.iso_13480_finish} welding={iso.iso_13458_welding} l={l} />}

      <p className="text-[10px] text-zinc-600 font-mono mt-3 leading-relaxed">
        {l.iso.footerDisclaimer}
      </p>
    </div>
  );
}

function IsoTableShell({ title, columns, children }) {
  return (
    <div className="border border-zinc-800 overflow-x-auto">
      <div className="px-3 py-2 bg-zinc-900 text-xs font-mono uppercase text-gold-400">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/60 text-[11px] font-mono uppercase text-zinc-500">
          <tr>{columns.map((c, i) => <th key={i} className={`px-3 py-2 ${c.align || "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Iso1127Table({ rows, filter, l }) {
  const filtered = filter ? rows.filter((r) => String(r.od).includes(filter)) : rows;
  return (
    <IsoTableShell
      title={l.iso.title1127}
      columns={[
        { label: l.iso.colOdMm },
        { label: "T1", align: "text-right" },
        { label: "T2", align: "text-right" },
        { label: "T3", align: "text-right" },
        { label: "T4", align: "text-right" },
      ]}
    >
      {filtered.map((r) => (
        <tr key={r.od} className="border-t border-zinc-800 hover:bg-zinc-900/40">
          <td className="px-3 py-1.5 font-display font-bold text-gold-400">⌀ {r.od}</td>
          <td className="px-3 py-1.5 text-right font-mono">{r.t1}</td>
          <td className="px-3 py-1.5 text-right font-mono">{r.t2}</td>
          <td className="px-3 py-1.5 text-right font-mono">{r.t3}</td>
          <td className="px-3 py-1.5 text-right font-mono">{r.t4}</td>
        </tr>
      ))}
      {filtered.length === 0 && (
        <tr><td colSpan="5" className="px-3 py-6 text-center text-zinc-600 text-sm">{l.iso.noResults(filter)}</td></tr>
      )}
    </IsoTableShell>
  );
}

function Iso4200Table({ rows, filter, l }) {
  const filtered = filter
    ? rows.filter((r) => String(r.od).includes(filter) || String(r.t).includes(filter))
    : rows;
  return (
    <IsoTableShell
      title={l.iso.title4200}
      columns={[
        { label: l.iso.colOdMm },
        { label: l.iso.colWallMm, align: "text-right" },
        { label: l.iso.colKgM, align: "text-right" },
      ]}
    >
      {filtered.map((r, i) => (
        <tr key={`${r.od}-${r.t}-${i}`} className="border-t border-zinc-800 hover:bg-zinc-900/40">
          <td className="px-3 py-1.5 font-display font-bold text-gold-400">⌀ {r.od}</td>
          <td className="px-3 py-1.5 text-right font-mono">{r.t}</td>
          <td className="px-3 py-1.5 text-right font-mono text-white">{r.mass_kg_m}</td>
        </tr>
      ))}
      {filtered.length === 0 && (
        <tr><td colSpan="3" className="px-3 py-6 text-center text-zinc-600 text-sm">{l.iso.noResults(filter)}</td></tr>
      )}
    </IsoTableShell>
  );
}

function Iso2768Tables({ linear, angular, l }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <IsoTableShell
        title={l.iso.title2768lin}
        columns={[
          { label: l.iso.colRange },
          { label: l.iso.colFineLin, align: "text-right" },
          { label: l.iso.colMedLin, align: "text-right" },
          { label: l.iso.colCoarseLin, align: "text-right" },
          { label: l.iso.colVeryCoarseLin, align: "text-right" },
        ]}
      >
        {linear.map((r, i) => (
          <tr key={i} className="border-t border-zinc-800">
            <td className="px-3 py-1.5 font-mono text-zinc-300">{r.range}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.f}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.m}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.c}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.v}</td>
          </tr>
        ))}
      </IsoTableShell>
      <IsoTableShell
        title={l.iso.title2768ang}
        columns={[
          { label: l.iso.colShortSide },
          { label: l.iso.colHFine, align: "text-right" },
          { label: l.iso.colKMed, align: "text-right" },
          { label: l.iso.colLCoarse, align: "text-right" },
        ]}
      >
        {angular.map((r, i) => (
          <tr key={i} className="border-t border-zinc-800">
            <td className="px-3 py-1.5 font-mono text-zinc-300">{r.range}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.H}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.K}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.L}</td>
          </tr>
        ))}
      </IsoTableShell>
    </div>
  );
}

function Iso6708Table({ rows, filter, l }) {
  const filtered = filter
    ? rows.filter((r) =>
        String(r.dn).includes(filter) ||
        String(r.nps).toLowerCase().includes(filter) ||
        String(r.od).includes(filter))
    : rows;
  return (
    <IsoTableShell
      title={l.iso.title6708}
      columns={[
        { label: l.iso.colDn },
        { label: l.iso.colNps, align: "text-right" },
        { label: l.iso.colOdMm, align: "text-right" },
      ]}
    >
      {filtered.map((r) => (
        <tr key={r.dn} className="border-t border-zinc-800 hover:bg-zinc-900/40">
          <td className="px-3 py-1.5 font-display font-bold text-gold-400">DN{r.dn}</td>
          <td className="px-3 py-1.5 text-right font-mono">{r.nps}″</td>
          <td className="px-3 py-1.5 text-right font-mono">{r.od}</td>
        </tr>
      ))}
      {filtered.length === 0 && (
        <tr><td colSpan="3" className="px-3 py-6 text-center text-zinc-600 text-sm">{l.iso.noResults(filter)}</td></tr>
      )}
    </IsoTableShell>
  );
}

function Iso15649Table({ rows, l }) {
  return (
    <IsoTableShell
      title={l.iso.title15649}
      columns={[
        { label: l.iso.colPn },
        { label: l.iso.colAnsiClass },
        { label: l.iso.colUseCase },
      ]}
    >
      {rows.map((r) => (
        <tr key={r.pn} className="border-t border-zinc-800 hover:bg-zinc-900/40">
          <td className="px-3 py-1.5 font-display font-bold text-gold-400">PN{r.pn}</td>
          <td className="px-3 py-1.5 font-mono text-zinc-300">{r.ansi_class}</td>
          <td className="px-3 py-1.5 text-zinc-400 text-xs">{l.pnUse[r.pn] || r.use}</td>
        </tr>
      ))}
    </IsoTableShell>
  );
}

function Iso13480Tables({ finish, welding, l }) {
  return (
    <div className="space-y-4">
      <IsoTableShell
        title={l.iso.title13480}
        columns={[
          { label: l.iso.colCode },
          { label: l.iso.colRaMax, align: "text-right" },
          { label: l.iso.colMethod },
          { label: l.iso.colUse },
        ]}
      >
        {finish.map((r) => (
          <tr key={r.code} className="border-t border-zinc-800">
            <td className="px-3 py-1.5 font-display font-bold text-gold-400">{r.code}</td>
            <td className="px-3 py-1.5 text-right font-mono text-white">{r.ra_max}</td>
            <td className="px-3 py-1.5 text-zinc-300 text-xs">{l.finishMethod[r.code] || r.method}</td>
            <td className="px-3 py-1.5 text-zinc-400 text-xs">{l.finishUse[r.code] || r.use}</td>
          </tr>
        ))}
      </IsoTableShell>
      <IsoTableShell
        title={l.iso.title13458}
        columns={[
          { label: l.iso.colClass },
          { label: l.iso.colDesc },
          { label: l.iso.colDefects },
          { label: l.iso.colUse },
        ]}
      >
        {welding.map((r) => (
          <tr key={r.class} className="border-t border-zinc-800">
            <td className="px-3 py-1.5 font-display font-black text-gold-400 text-lg">{r.class}</td>
            <td className="px-3 py-1.5 text-zinc-300 text-xs">{l.weldDesc[r.class] || r.desc}</td>
            <td className="px-3 py-1.5 font-mono text-xs">{l.weldDefects[r.class] || r.defects}</td>
            <td className="px-3 py-1.5 text-zinc-400 text-xs">{l.weldUse[r.class] || r.use}</td>
          </tr>
        ))}
      </IsoTableShell>
    </div>
  );
}
